// Shared half-fill dot glyph. Originally inline in
// milestone/ProgressDots.tsx (Partial accepted state) + replicated in
// deliverable/DeliverableLifecycleTimeline.tsx (Conditionally accepted
// node). Extracted here so both surfaces share the visual contract —
// any change to the half-fill geometry / palette propagates without
// drift. `size="sm"` (10px) matches the milestone progress-dot
// register; `size="md"` (12px) matches the lifecycle-timeline node
// register.

import { clsx } from "clsx";

export interface AmberHalfFillDotProps {
  size?: "sm" | "md";
  /** Optional aria-label on the outer span. Outer span carries
   *  role="img" only when an ariaLabel is supplied. */
  ariaLabel?: string;
  /** Optional testid on the inner half-fill span (the amber-filled
   *  half-circle). Outer span gets no testid by default so callers
   *  can layer their own. */
  testId?: string;
  /** Optional title (browser tooltip) on the outer span. */
  title?: string;
  /** Optional testid on the OUTER span (the bordered circle).
   *  Distinct from `testId` which targets the inner fill — use this
   *  when a consumer wants to assert against the dot itself (e.g.
   *  ProgressDots' `step-dot-accepted` selector). */
  outerTestId?: string;
  /** Free-form data-* attributes layered on the outer span. */
  outerDataAttrs?: Record<string, string>;
}

const SIZE_CLASS: Record<NonNullable<AmberHalfFillDotProps["size"]>, string> = {
  sm: "h-2.5 w-2.5",
  md: "h-3 w-3",
};

export default function AmberHalfFillDot({
  size = "sm",
  ariaLabel,
  testId,
  title,
  outerTestId,
  outerDataAttrs,
}: AmberHalfFillDotProps) {
  return (
    <span
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
      title={title}
      data-testid={outerTestId}
      {...outerDataAttrs}
      className={clsx(
        SIZE_CLASS[size],
        "relative rounded-full border border-amber-400 bg-transparent dark:border-amber-400",
      )}
    >
      <span
        aria-hidden="true"
        className="absolute inset-0 overflow-hidden rounded-full"
      >
        <span
          data-testid={testId}
          className="absolute inset-y-0 left-0 w-1/2 bg-amber-400"
        />
      </span>
    </span>
  );
}
